import styled from 'styled-components'
import { match } from 'ts-pattern'

import { Lock1Icon, Tooltip } from '@blade/ui-kit'
import { auditToColor } from '@root/entities/pair/ui'
import { CssUtil } from '@root/shared'
import { formatLiquidityBurned } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import type { PairAudit } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type PoolLiquidityBadgeProps = {
  data: PairAudit.Audit['checks']['lpBurned']
} & ClassNameProps

export const PoolLiquidityBadge: React.FC<PoolLiquidityBadgeProps> = ({
  className,
  data,
}) => {
  const { t } = useTranslation()
  return (
    <Tooltip
      content={t('trading.audit.lp_burned.tooltip-percentage', {
        percents: `${formatLiquidityBurned(data.percent)}%`,
      })}
    >
      <Root
        $status={data.status}
        $percents={data.percent}
        className={className}
      >
        <Lock1Icon size={10} />
      </Root>
    </Tooltip>
  )
}

const Root = styled.div.attrs<{
  $status: PairAudit.AuditStatus
  $percents: number
}>(({ $status, $percents, theme }) => {
  const bgColor = match($status)
    .with('Safe', () => CssUtil.hexToRgbA(theme.palette.success[9], 0.2))
    .with('Warning', () => CssUtil.hexToRgbA(theme.palette.warning[9], 0.2))
    .with('Danger', () => CssUtil.hexToRgbA(theme.palette.error[9], 0.2))
    .exhaustive()
  const fillColor = auditToColor($status)

  return {
    style: {
      backgroundImage: `linear-gradient(180deg, ${bgColor} 0%, ${bgColor} ${100 - $percents}%, ${fillColor} ${100 - $percents}%, ${fillColor} 100%)`,
    },
  }
})`
  width: 14px;
  height: 14px;
  border-radius: 14px;
  color: var(--appearance-inv-1);
  display: grid;
  place-items: center;
  place-content: center;
`
